const hostOnlyRegex = /^([^/?#:]+:[^/?#]+)(.*)$/;

/**
 * Returns true when a path already begins with the tenant prefix.
 * Accepts prefix in normalised `/foo/bar` form.
 */
export const isPathPrefixed = (path: string, normalizedPrefix: string): boolean =>
  path.startsWith(`${normalizedPrefix}/`) || path === normalizedPrefix;

/**
 * Prefixes a URL with the tenant base-path when missing.
 *
 * Behaviour:
 * 1. If the URL already begins with the prefix → return untouched.
 * 2. Otherwise insert the prefix:
 *    • after the origin for absolute URLs
 *    • at start of the path for host-only / relative URLs
 *
 * Supported input shapes:
 *   • Full URL      – "https://example.com/api/lookup"
 *   • Host-only URL – "example.com:3000/api/lookup"
 *   • Absolute path – "/api/lookup"
 *   • Relative path – "api/lookup"
 */
export const addTenantPrefix = (url: string, prefix?: string) => {
  if (!url || !prefix) return url;

  const urlString = url.toString();
  const normalizedPrefix = prefix.startsWith('/') ? prefix : `/${prefix}`;

  // 1️⃣ Host-only URLs (contain ":" but no protocol)
  const hostMatch = urlString.match(hostOnlyRegex);

  if (hostMatch) {
    const [, host, path] = hostMatch;
    return isPathPrefixed(path, normalizedPrefix) ? urlString : `${host}${normalizedPrefix}${path}`;
  }

  try {
    // 2️⃣ Full URLs
    const parsed = new URL(urlString);
    return isPathPrefixed(parsed.pathname, normalizedPrefix)
      ? urlString
      : `${parsed.origin}${normalizedPrefix}${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    // 3️⃣ Paths (absolute or relative)
    if (urlString.startsWith('/')) {
      return isPathPrefixed(urlString, normalizedPrefix)
        ? urlString
        : `${normalizedPrefix}${urlString}`;
    }

    const withLeading = `/${urlString}`;
    return isPathPrefixed(withLeading, normalizedPrefix)
      ? urlString
      : `${normalizedPrefix}/${urlString}`;
  }
};
